import { useEffect, useState } from "react";
import { Users, Cpu, UserPlus, Trash2, ShieldAlert, Loader2, RotateCcw, Megaphone, Settings, Plus, X, Mail, Check, Copy, KeyRound, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

function formatLastLogin(iso) {
  if (!iso) return "Noch nie";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "Noch nie";
  }
}

export default function AdminArea() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("users");

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [courses, setCourses] = useState([]);
  const [moduleSelections, setModuleSelections] = useState({});
  const [resettingId, setResettingId] = useState(null);

  // Create User Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [creatingUser, setCreatingUser] = useState(false);

  // KI Settings State
  const [ollamaUrl, setOllamaUrl] = useState("");
  const [ollamaModel, setOllamaModel] = useState("llama3.2");
  const [ollamaCodingModel, setOllamaCodingModel] = useState("llama3.2");
  const [availableModels, setAvailableModels] = useState(["llama3.2:3b", "qwen2.5-coder:7b", "mistral:latest", "minicpm-v:latest", "qwen3:8b", "qwen3:4b-instruct"]);
  const [emergentKey, setEmergentKey] = useState("");
  const [emergentModel, setEmergentModel] = useState("claude-sonnet-4-6");
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Ads Settings State
  const [adsEnabled, setAdsEnabled] = useState(false);
  const [dashboardSnippet, setDashboardSnippet] = useState("");
  const [coursesSnippet, setCoursesSnippet] = useState("");
  const [lessonSnippet, setLessonSnippet] = useState("");
  const [playgroundSnippet, setPlaygroundSnippet] = useState("");
  const [leaderboardLeftSnippet, setLeaderboardLeftSnippet] = useState("");
  const [leaderboardRightSnippet, setLeaderboardRightSnippet] = useState("");
  const [landingSnippet, setLandingSnippet] = useState("");
  const [werbeseiteSnippets, setWerbeseiteSnippets] = useState([]);
  const [popunderSnippet, setPopunderSnippet] = useState("");
  const [smartlinkUrl, setSmartlinkUrl] = useState("");
  const [smartlinkLabel, setSmartlinkLabel] = useState("");
  const [loadingAds, setLoadingAds] = useState(true);
  const [savingAds, setSavingAds] = useState(false);

  // General Settings State
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [imapUser, setImapUser] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  const [loadingGeneral, setLoadingGeneral] = useState(true);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingImap, setSavingImap] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingImap, setTestingImap] = useState(false);

  // Access Requests State
  const [accessRequests, setAccessRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [approvedInfo, setApprovedInfo] = useState(null); // { email, password }
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approveRole, setApproveRole] = useState("user");
  const [approveSendEmail, setApproveSendEmail] = useState(true);

  // Editing User State
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("user");

  const handleEditUser = (u) => {
    setEditName(u.name);
    setEditRole(u.role);
    setEditingUser(u);
  };

  const confirmEditUser = async () => {
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        name: editName,
        role: editRole
      });
      toast.success("Benutzer erfolgreich aktualisiert! 👤");
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Aktualisieren des Benutzers");
    }
  };

  useEffect(() => {
    if (user?.role === "admin") {
      fetchUsers();
      fetchSettings();
      fetchAdsSettings();
      fetchGeneralSettings();
      fetchAccessRequests();
      api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    }
  }, [user]);

  const fetchAccessRequests = async () => {
    setLoadingRequests(true);
    try {
      const { data } = await api.get("/admin/access-requests");
      setAccessRequests(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Laden der Zugangsanfragen");
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleApproveRequest = (req) => {
    setApproveRole("user");
    setApproveSendEmail(true);
    setApprovingRequest(req);
  };

  const confirmApproveRequest = async () => {
    if (!approvingRequest) return;
    const reqId = approvingRequest.id;
    setReviewingId(reqId);
    try {
      const { data } = await api.post(`/admin/access-requests/${reqId}/approve`, {
        role: approveRole,
        send_email: approveSendEmail
      });
      setApprovedInfo({ email: data.user.email, password: data.temporary_password });
      setApprovingRequest(null);
      fetchAccessRequests();
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Genehmigen");
    } finally {
      setReviewingId(null);
    }
  };

  const handleRejectRequest = async (req) => {
    if (!confirm(`Anfrage von "${req.name}" (${req.email}) wirklich ablehnen?`)) return;
    setReviewingId(req.id);
    try {
      await api.post(`/admin/access-requests/${req.id}/reject`);
      toast.success("Anfrage abgelehnt.");
      fetchAccessRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Ablehnen");
    } finally {
      setReviewingId(null);
    }
  };

  const handleDeleteRequest = async (req) => {
    if (!confirm(`Zugangsanfrage von "${req.name}" (${req.email}) wirklich löschen?`)) return;
    setReviewingId(req.id);
    try {
      await api.delete(`/admin/access-requests/${req.id}`);
      toast.success("Anfrage gelöscht.");
      fetchAccessRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Löschen");
    } finally {
      setReviewingId(null);
    }
  };

  const copyPassword = async () => {
    if (!approvedInfo) return;
    try {
      await navigator.clipboard.writeText(approvedInfo.password);
      toast.success("Passwort kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich – bitte manuell markieren.");
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Laden der Benutzer");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const { data } = await api.get("/admin/settings/ki");
      setOllamaUrl(data.ollama_base_url || "");
      setOllamaModel(data.ollama_model || "llama3.2");
      setOllamaCodingModel(data.ollama_coding_model || "llama3.2");
      setEmergentKey(data.emergent_llm_key || "");
      setEmergentModel(data.emergent_fallback_model || "claude-sonnet-4-6");
      
      try {
        const modelsRes = await api.get("/admin/settings/ki/models");
        if (modelsRes.data && modelsRes.data.length > 0) {
          setAvailableModels(modelsRes.data);
        }
      } catch (err) {
        console.error("Failed to load ollama models", err);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Laden der Einstellungen");
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchAdsSettings = async () => {
    setLoadingAds(true);
    try {
      const { data } = await api.get("/admin/settings/ads");
      setAdsEnabled(!!data.enabled);
      setDashboardSnippet(data.dashboard_snippet || "");
      setCoursesSnippet(data.courses_snippet || "");
      setLessonSnippet(data.lesson_snippet || "");
      setPlaygroundSnippet(data.playground_snippet || "");
      setLeaderboardLeftSnippet(data.leaderboard_left_snippet || "");
      setLeaderboardRightSnippet(data.leaderboard_right_snippet || "");
      setLandingSnippet(data.landing_snippet || "");
      setWerbeseiteSnippets(data.werbeseite_snippets && data.werbeseite_snippets.length ? data.werbeseite_snippets : [""]);
      setPopunderSnippet(data.popunder_snippet || "");
      setSmartlinkUrl(data.smartlink_url || "");
      setSmartlinkLabel(data.smartlink_label || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Laden der Werbe-Einstellungen");
    } finally {
      setLoadingAds(false);
    }
  };

  const fetchGeneralSettings = async () => {
    setLoadingGeneral(true);
    try {
      const { data } = await api.get("/admin/settings/general");
      setRegistrationEnabled(data.registration_enabled !== false);
      setSmtpHost(data.smtp_host || "");
      setSmtpPort(data.smtp_port || 465);
      setSmtpUser(data.smtp_user || "");
      setSmtpPassword(data.smtp_password || "");
      setSmtpFrom(data.smtp_from || "");
      setImapHost(data.imap_host || "");
      setImapPort(data.imap_port || 993);
      setImapUser(data.imap_user || "");
      setImapPassword(data.imap_password || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Laden der Einstellungen");
    } finally {
      setLoadingGeneral(false);
    }
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSavingSmtp(true);
    try {
      await api.post("/admin/settings/general", {
        registration_enabled: registrationEnabled,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 465,
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from: smtpFrom,
        imap_host: imapHost,
        imap_port: parseInt(imapPort) || 993,
        imap_user: imapUser,
        imap_password: imapPassword,
      });
      toast.success("SMTP-E-Mail-Einstellungen erfolgreich gespeichert! ✉️");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSaveImap = async (e) => {
    e.preventDefault();
    setSavingImap(true);
    try {
      await api.post("/admin/settings/general", {
        registration_enabled: registrationEnabled,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 465,
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from: smtpFrom,
        imap_host: imapHost,
        imap_port: parseInt(imapPort) || 993,
        imap_user: imapUser,
        imap_password: imapPassword,
      });
      toast.success("IMAP-Einstellungen erfolgreich gespeichert! 📥");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSavingImap(false);
    }
  };

  const handleTestSmtp = async (e) => {
    e.preventDefault();
    setTestingSmtp(true);
    try {
      const { data } = await api.post("/admin/settings/general/test-smtp", {
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 465,
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
      });
      toast.success(data.message || "SMTP Verbindung erfolgreich! 🎉");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "SMTP Verbindung fehlgeschlagen.");
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTestImap = async (e) => {
    e.preventDefault();
    setTestingImap(true);
    try {
      const { data } = await api.post("/admin/settings/general/test-imap", {
        imap_host: imapHost,
        imap_port: parseInt(imapPort) || 993,
        imap_user: imapUser,
        imap_password: imapPassword,
      });
      toast.success(data.message || "IMAP Verbindung erfolgreich! 🎉");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "IMAP Verbindung fehlgeschlagen.");
    } finally {
      setTestingImap(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      const payload = {
        name: newName,
        email: newEmail,
        password: newPassword,
        role: newRole,
      };
      await api.post("/admin/users", payload);
      toast.success("Benutzer erfolgreich angelegt! 🎉");
      setNewName("");
      setNewEmail("");
      setNewPassword("");
      setNewRole("user");
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Anlegen des Benutzers");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!confirm(`Soll der Benutzer "${name}" wirklich geloescht werden?`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Benutzer erfolgreich geloescht.");
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Loeschen des Benutzers");
    }
  };

  const handleTriggerReset = async (u) => {
    if (!confirm(`Soll wirklich ein Link zum Zurücksetzen des Passworts an "${u.name}" (${u.email}) gesendet werden?`)) {
      return;
    }
    try {
      await api.post(`/admin/users/${u.id}/trigger-reset`);
      toast.success(`Passwort-Reset-Link wurde an ${u.email} gesendet! 🔑`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Senden des Reset-Links");
    }
  };

  const handleResetUser = async (userId, name) => {
    if (!confirm(`Sollen wirklich ALLE Daten von "${name}" zurueckgesetzt werden? XP, Streak, Lektionen, Quizze, Challenges, die Tages-Challenge-Serie und alle Flashcards gehen unwiderruflich verloren. Das Konto selbst bleibt erhalten.`)) {
      return;
    }
    setResettingId(userId);
    try {
      await api.post(`/admin/users/${userId}/reset`);
      toast.success(`Fortschritt von "${name}" wurde zurueckgesetzt.`);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Zuruecksetzen");
    } finally {
      setResettingId(null);
    }
  };

  const handleResetModule = async (userId, name) => {
    const slug = moduleSelections[userId];
    if (!slug) {
      toast.error("Bitte zuerst ein Modul auswaehlen.");
      return;
    }
    const course = courses.find((c) => c.slug === slug);
    if (!confirm(`Soll der Kurs "${course?.title || slug}" fuer "${name}" zurueckgesetzt werden?`)) {
      return;
    }
    setResettingId(userId);
    try {
      await api.post(`/admin/users/${userId}/reset-module/${slug}`);
      toast.success(`Kurs "${course?.title || slug}" fuer "${name}" wurde zurueckgesetzt.`);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Zuruecksetzen");
    } finally {
      setResettingId(null);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const payload = {
        ollama_base_url: ollamaUrl,
        ollama_model: ollamaModel,
        ollama_coding_model: ollamaCodingModel,
        emergent_llm_key: emergentKey,
        emergent_fallback_model: emergentModel,
      };
      await api.post("/admin/settings/ki", payload);
      toast.success("KI-Server-Einstellungen erfolgreich gespeichert! 🤖");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern der Einstellungen");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveAds = async (e) => {
    e.preventDefault();
    setSavingAds(true);
    try {
      const payload = {
        enabled: adsEnabled,
        dashboard_snippet: dashboardSnippet,
        courses_snippet: coursesSnippet,
        lesson_snippet: lessonSnippet,
        playground_snippet: playgroundSnippet,
        leaderboard_left_snippet: leaderboardLeftSnippet,
        leaderboard_right_snippet: leaderboardRightSnippet,
        landing_snippet: landingSnippet,
        werbeseite_snippets: werbeseiteSnippets.filter((s) => s.trim()),
        popunder_snippet: popunderSnippet,
        smartlink_url: smartlinkUrl,
        smartlink_label: smartlinkLabel,
      };
      await api.post("/admin/settings/ads", payload);
      toast.success("Werbe-Einstellungen erfolgreich gespeichert! 📣");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern der Werbe-Einstellungen");
    } finally {
      setSavingAds(false);
    }
  };

  const updateWerbeseiteSnippet = (idx, value) => {
    setWerbeseiteSnippets((prev) => prev.map((s, i) => (i === idx ? value : s)));
  };

  const addWerbeseiteSnippet = () => {
    setWerbeseiteSnippets((prev) => [...prev, ""]);
  };

  const removeWerbeseiteSnippet = (idx) => {
    setWerbeseiteSnippets((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveGeneral = async (enabled) => {
    setRegistrationEnabled(enabled);
    setSavingGeneral(true);
    try {
      await api.post("/admin/settings/general", {
        registration_enabled: enabled,
      });
      toast.success(enabled ? "Registrierung ist jetzt offen." : "Registrierung ist jetzt geschlossen.");
    } catch (err) {
      setRegistrationEnabled(!enabled);
      toast.error(formatApiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSavingGeneral(false);
    }
  };

  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-8" data-testid="admin-page">
      {approvedInfo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approved-password-modal">
          <div className="nb-card bg-white p-6 sm:p-8 w-full max-w-md text-center space-y-4">
            <div className="h-12 w-12 mx-auto bg-success border-2 border-black rounded-xl flex items-center justify-center nb-shadow-sm">
              <KeyRound className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <h2 className="font-heading font-black text-xl">Konto angelegt für {approvedInfo.email}</h2>
            <p className="text-sm font-medium text-muted-foreground">
              Dieses Passwort wird nur einmal angezeigt. Teile es der Person außerhalb der Plattform mit (z.B. per E-Mail).
            </p>
            <div className="flex items-center gap-2 bg-muted border-2 border-black rounded-lg px-3 py-2.5 font-mono font-bold text-sm justify-between">
              <span data-testid="approved-password-value">{approvedInfo.password}</span>
              <button onClick={copyPassword} title="Kopieren" className="shrink-0">
                <Copy className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
            <button
              onClick={() => setApprovedInfo(null)}
              data-testid="approved-password-close"
              className="bg-primary text-white font-bold px-5 py-2.5 rounded-xl border-2 border-black nb-shadow nb-press w-full"
            >
              Schließen
            </button>
          </div>
        </div>
      )}

      {approvingRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approve-request-modal">
          <div className="nb-card bg-white p-6 sm:p-8 w-full max-w-md text-left space-y-4 border-2 border-black rounded-2xl nb-shadow-lg animate-pop">
            <h2 className="font-heading font-black text-xl">Zugangsanfrage genehmigen</h2>
            <p className="text-sm font-medium text-muted-foreground">
              Wähle die Rolle und ob das Einmal-Passwort automatisch per E-Mail an <strong>{approvingRequest.name}</strong> ({approvingRequest.email}) gesendet werden soll.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase mb-1">Rolle</label>
                <select
                  value={approveRole}
                  onChange={(e) => setApproveRole(e.target.value)}
                  className="w-full border-2 border-black rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white font-bold"
                >
                  <option value="user">Benutzer (user)</option>
                  <option value="admin">Administrator (admin)</option>
                </select>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="sendEmailCheckbox"
                  checked={approveSendEmail}
                  onChange={(e) => setApproveSendEmail(e.target.checked)}
                  className="h-4 w-4 border-2 border-black rounded focus:ring-0 focus:outline-none accent-primary cursor-pointer"
                />
                <label htmlFor="sendEmailCheckbox" className="text-sm font-bold cursor-pointer select-none">
                  Einmal-Passwort per E-Mail senden
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setApprovingRequest(null)}
                className="flex-1 bg-muted text-black font-bold px-5 py-2.5 rounded-xl border-2 border-black nb-shadow nb-press"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmApproveRequest}
                disabled={reviewingId !== null}
                className="flex-1 bg-success text-black font-bold px-5 py-2.5 rounded-xl border-2 border-black nb-shadow nb-press flex items-center justify-center gap-1.5"
              >
                {reviewingId !== null && <Loader2 className="h-4 w-4 animate-spin" />}
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
          <div className="nb-card bg-white p-6 sm:p-8 w-full max-w-md text-left space-y-4 border-2 border-black rounded-2xl nb-shadow-lg animate-pop">
            <h2 className="font-heading font-black text-xl">Benutzer bearbeiten</h2>
            <p className="text-sm font-medium text-muted-foreground">
              Bearbeite den Namen und die Rolle für <strong>{editingUser.email}</strong>.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase mb-1">Name</label>
                <input
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full border-2 border-black rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-bold"
                  placeholder="Name"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase mb-1">Rolle</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="w-full border-2 border-black rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white font-bold"
                >
                  <option value="user">Benutzer (user)</option>
                  <option value="admin">Administrator (admin)</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEditingUser(null)}
                className="flex-1 bg-muted text-black font-bold px-5 py-2.5 rounded-xl border-2 border-black nb-shadow nb-press"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmEditUser}
                className="flex-1 bg-primary text-white font-bold px-5 py-2.5 rounded-xl border-2 border-black nb-shadow nb-press flex items-center justify-center gap-1.5"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="nb-card p-6 bg-secondary text-white border-2 border-black">
        <h1 className="font-heading font-black text-3xl tracking-tight flex items-center gap-2">
          <ShieldAlert className="h-8 w-8" /> Admin-Bereich
        </h1>
        <p className="font-medium opacity-90 mt-1">Verwalte Benutzer, Systemrollen und die KI-Server-Konfigurationen fuer DevPath.</p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 border-b-4 border-black pb-4">
        <button
          onClick={() => setActiveTab("users")}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 border-2 border-black rounded-xl font-heading font-black text-xs sm:text-sm nb-shadow-sm nb-press transition-colors ${
            activeTab === "users"
              ? "bg-primary text-white"
              : "bg-white text-black hover:bg-muted/10"
          }`}
        >
          <Users className="h-4 w-4" />
          Benutzer & Anfragen
        </button>
        <button
          onClick={() => setActiveTab("general")}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 border-2 border-black rounded-xl font-heading font-black text-xs sm:text-sm nb-shadow-sm nb-press transition-colors ${
            activeTab === "general"
              ? "bg-primary text-white"
              : "bg-white text-black hover:bg-muted/10"
          }`}
        >
          <Settings className="h-4 w-4" />
          Allgemein & E-Mail
        </button>
        <button
          onClick={() => setActiveTab("ki")}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 border-2 border-black rounded-xl font-heading font-black text-xs sm:text-sm nb-shadow-sm nb-press transition-colors ${
            activeTab === "ki"
              ? "bg-primary text-white"
              : "bg-white text-black hover:bg-muted/10"
          }`}
        >
          <Cpu className="h-4 w-4" />
          KI-Server
        </button>
        <button
          onClick={() => setActiveTab("ads")}
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 border-2 border-black rounded-xl font-heading font-black text-xs sm:text-sm nb-shadow-sm nb-press transition-colors ${
            activeTab === "ads"
              ? "bg-primary text-white"
              : "bg-white text-black hover:bg-muted/10"
          }`}
        >
          <Megaphone className="h-4 w-4" />
          Werbung
        </button>
      </div>

      {activeTab === "users" && (
        <div className="space-y-6 animate-fade-in">
          {/* User Management */}
          <div className="nb-card p-6 bg-white border-2 border-black">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-black text-2xl flex items-center gap-2">
                <Users className="h-6 w-6 text-primary" /> Benutzerverwaltung
              </h2>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="px-3 py-1.5 bg-accent text-black border-2 border-black rounded-lg font-bold text-xs flex items-center gap-1.5 nb-shadow-sm nb-press"
              >
                <UserPlus className="h-3.5 w-3.5" /> {showAddForm ? "Abbrechen" : "Benutzer anlegen"}
              </button>
            </div>

            {/* Create User Form */}
            {showAddForm && (
              <form onSubmit={handleCreateUser} className="mb-6 p-4 border-2 border-dashed border-black rounded-xl bg-muted/30 space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold mb-1">Name</label>
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full px-3 py-1.5 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="z.B. Anna Schmidt"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">E-Mail</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-3 py-1.5 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="anna@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold mb-1">Passwort</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-1.5 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="Min. 10 Zeichen"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">Rolle</label>
                    <select
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      className="w-full px-3 py-1.5 border-2 border-black rounded-lg text-sm font-bold"
                    >
                      <option value="user">Benutzer (user)</option>
                      <option value="admin">Administrator (admin)</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={creatingUser}
                  className="px-4 py-2 bg-primary text-white border-2 border-black rounded-lg font-bold text-xs nb-shadow-sm nb-press"
                >
                  {creatingUser ? "Wird erstellt..." : "Speichern"}
                </button>
              </form>
            )}

            {/* Users Table */}
            {loadingUsers ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-black text-xs font-black uppercase text-muted-foreground">
                      <th className="py-2 px-3">Name</th>
                      <th className="py-2 px-3">Rolle</th>
                      <th className="py-2 px-3 text-center">XP</th>
                      <th className="py-2 px-3 text-center">Streak</th>
                      <th className="py-2 px-3">Modul</th>
                      <th className="py-2 px-3">Letzter Login</th>
                      <th className="py-2 px-3 text-right sticky right-0 bg-white">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-muted font-bold text-sm hover:bg-muted/20">
                        <td className="py-3 px-3">
                          <div>{u.name}</div>
                          <div className="text-[11px] font-medium text-muted-foreground normal-case">{u.email}</div>
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 border border-black rounded-md text-[10px] uppercase ${u.role === "admin" ? "bg-destructive text-white" : "bg-muted"}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center text-primary">{u.xp}</td>
                        <td className="py-3 px-3 text-center text-warning">{u.streak} 🔥</td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1">
                            <select
                              value={moduleSelections[u.id] || ""}
                              onChange={(e) => setModuleSelections((prev) => ({ ...prev, [u.id]: e.target.value }))}
                              className="px-1.5 py-1 border-2 border-black rounded-md text-xs font-bold max-w-[110px]"
                            >
                              <option value="">Modul...</option>
                              {courses.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.title}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleResetModule(u.id, u.name)}
                              disabled={resettingId === u.id || !moduleSelections[u.id]}
                              className="p-1 border border-transparent hover:border-black hover:bg-warning rounded-md disabled:opacity-30 shrink-0"
                              title="Modul fuer diesen Nutzer zuruecksetzen"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-xs font-medium text-muted-foreground normal-case whitespace-nowrap">
                          {formatLastLogin(u.last_login)}
                        </td>
                        <td className="py-3 px-3 text-right sticky right-0 bg-white">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleEditUser(u)}
                              className="p-1 border border-transparent hover:border-black hover:bg-secondary hover:text-white rounded-md"
                              title="Benutzer bearbeiten"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleTriggerReset(u)}
                              className="p-1 border border-transparent hover:border-black hover:bg-primary hover:text-white rounded-md"
                              title="Passwort-Reset-Link per E-Mail senden"
                            >
                              <KeyRound className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleResetUser(u.id, u.name)}
                              disabled={resettingId === u.id}
                              className="p-1 border border-transparent hover:border-black hover:bg-warning rounded-md disabled:opacity-30"
                              title="Alle Daten zuruecksetzen (XP, Streak, Lektionen, Quizze, Challenges, Flashcards)"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(u.id, u.name)}
                              disabled={user?.id === u.id}
                              className="p-1 border border-transparent hover:border-black hover:bg-destructive hover:text-white rounded-md disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-current"
                              title={user?.id === u.id ? "Sie koennen sich nicht selbst loeschen" : "Benutzer loeschen"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Access Requests */}
          <div className="nb-card p-6 bg-white border-2 border-black">
            <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" /> Zugangsanfragen
            </h2>
            {loadingRequests ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : accessRequests.length === 0 ? (
              <p className="text-sm text-muted-foreground font-medium">Noch keine Zugangsanfragen eingegangen.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-black text-xs font-black uppercase text-muted-foreground">
                      <th className="py-2 px-3">Name</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3 text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessRequests.map((r) => (
                      <tr key={r.id} className="border-b border-muted font-bold text-sm hover:bg-muted/20">
                        <td className="py-3 px-3">
                          <div>{r.name}</div>
                          <div className="text-[11px] font-medium text-muted-foreground normal-case">{r.email}</div>
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`px-2 py-0.5 border border-black rounded-md text-[10px] uppercase ${
                              r.status === "approved" ? "bg-success" : r.status === "rejected" ? "bg-destructive text-white" : "bg-warning"
                            }`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          {r.status === "pending" ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleApproveRequest(r)}
                                disabled={reviewingId === r.id}
                                className="p-1 border border-transparent hover:border-black hover:bg-success rounded-md disabled:opacity-30"
                                title="Genehmigen & Konto anlegen"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleRejectRequest(r)}
                                disabled={reviewingId === r.id}
                                className="p-1 border border-transparent hover:border-black hover:bg-destructive hover:text-white rounded-md disabled:opacity-30"
                                title="Ablehnen"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleDeleteRequest(r)}
                                disabled={reviewingId === r.id}
                                className="p-1 border border-transparent hover:border-black hover:bg-destructive hover:text-white rounded-md disabled:opacity-30"
                                title="Löschen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "general" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
          {/* General */}
          <div className="nb-card p-6 bg-white border-2 border-black h-fit">
            <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
              <Settings className="h-6 w-6 text-primary" /> Allgemein
            </h2>

            {loadingGeneral ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-bold text-sm">Registrierung erlauben</div>
                  <div className="text-[10px] font-medium text-muted-foreground">
                    Wenn aus, koennen sich keine neuen Nutzer mehr anmelden.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleSaveGeneral(!registrationEnabled)}
                  disabled={savingGeneral}
                  data-testid="toggle-registration"
                  className={`relative h-7 w-12 shrink-0 rounded-full border-2 border-black p-0 transition-colors disabled:opacity-50 ${
                    registrationEnabled ? "bg-success" : "bg-muted"
                  }`}
                >
                  <span
                    className={`absolute left-0.5 top-0.5 h-4 w-4 bg-white border-2 border-black rounded-full transition-transform ${
                      registrationEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            )}
          </div>

          {/* Right Column: E-Mail Settings (SMTP + IMAP stacked) */}
          <div className="space-y-6">
            {/* E-Mail Server (SMTP) */}
            <div className="nb-card p-6 bg-white border-2 border-black h-fit">
              <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
                <Mail className="h-6 w-6 text-primary" /> E-Mail Server (SMTP)
              </h2>

              {loadingGeneral ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <form onSubmit={handleSaveSmtp} className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">SMTP-Host</label>
                    <input
                      type="text"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="smtp.ionos.de"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">SMTP-Port</label>
                    <input
                      type="number"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="465"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">SMTP-Benutzername (E-Mail)</label>
                    <input
                      type="email"
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="noreply@kaidra.app"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">SMTP-Passwort</label>
                    <input
                      type="password"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="E-Mail Passwort"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">Absender-E-Mail (From)</label>
                    <input
                      type="email"
                      value={smtpFrom}
                      onChange={(e) => setSmtpFrom(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="noreply@kaidra.app"
                      required
                    />
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground block">
                    Diese Zugangsdaten werden für den Versand von Zugangs- und Passwort-Reset-E-Mails verwendet.
                  </span>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleTestSmtp}
                      disabled={testingSmtp}
                      className="flex-1 py-2 bg-accent text-black border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {testingSmtp && <Loader2 className="h-4 w-4 animate-spin" />}
                      Verbindung testen
                    </button>
                    <button
                      type="submit"
                      disabled={savingSmtp}
                      className="flex-1 py-2 bg-primary text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50"
                    >
                      {savingSmtp ? "Speichern..." : "Speichern"}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Posteingangs-Server (IMAP) */}
            <div className="nb-card p-6 bg-white border-2 border-black h-fit">
              <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
                <Mail className="h-6 w-6 text-primary" /> Posteingangs-Server (IMAP)
              </h2>

              {loadingGeneral ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <form onSubmit={handleSaveImap} className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">IMAP-Host</label>
                    <input
                      type="text"
                      value={imapHost}
                      onChange={(e) => setImapHost(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="imap.ionos.de"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">IMAP-Port</label>
                    <input
                      type="number"
                      value={imapPort}
                      onChange={(e) => setImapPort(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="993"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">IMAP-Benutzername</label>
                    <input
                      type="email"
                      value={imapUser}
                      onChange={(e) => setImapUser(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="noreply@kaidra.app"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">IMAP-Passwort</label>
                    <input
                      type="password"
                      value={imapPassword}
                      onChange={(e) => setImapPassword(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                      placeholder="IMAP Passwort"
                      required
                    />
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground block">
                    Diese Einstellungen werden verwendet, um die Erreichbarkeit des Posteingangs zu testen.
                  </span>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleTestImap}
                      disabled={testingImap}
                      className="flex-1 py-2 bg-accent text-black border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {testingImap && <Loader2 className="h-4 w-4 animate-spin" />}
                      Verbindung testen
                    </button>
                    <button
                      type="submit"
                      disabled={savingImap}
                      className="flex-1 py-2 bg-primary text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press disabled:opacity-50"
                    >
                      {savingImap ? "Speichern..." : "Speichern"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "ki" && (
        <div className="max-w-3xl animate-fade-in">
          <div className="nb-card p-6 bg-white border-2 border-black">
            <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
              <Cpu className="h-6 w-6 text-primary" /> KI-Server Einstellungen
            </h2>

            {loadingSettings ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <form onSubmit={handleSaveSettings} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold mb-1">Ollama Base URL</label>
                  <input
                    type="url"
                    value={ollamaUrl}
                    onChange={(e) => setOllamaUrl(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                    placeholder="http://localhost:11434"
                    required
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Adresse deines lokalen Ollama-Servers (z.B. http://192.168.x.x:11434).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Ollama Model (Standard für Fragen)</label>
                  <select
                    value={ollamaModel}
                    onChange={(e) => setOllamaModel(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold bg-white"
                    required
                  >
                    {availableModels.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Ollama Model (für Programmieraufgaben/Code)</label>
                  <select
                    value={ollamaCodingModel}
                    onChange={(e) => setOllamaCodingModel(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold bg-white"
                    required
                  >
                    {availableModels.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Emergent LLM API Key (Fallback)</label>
                  <input
                    type="password"
                    value={emergentKey}
                    onChange={(e) => setEmergentKey(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                    placeholder="Wird zur Sicherheit verschleiert"
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Optionaler API-Key fuer Claude/Anthropic-Fallback.
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Emergent Fallback Modell</label>
                  <input
                    type="text"
                    value={emergentModel}
                    onChange={(e) => setEmergentModel(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                    placeholder="claude-sonnet-4-6"
                  />
                </div>

                <button
                  type="submit"
                  disabled={savingSettings}
                  className="w-full py-2.5 bg-primary text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press hover:bg-black hover:text-white transition-all disabled:opacity-50"
                >
                  {savingSettings ? "Speichern..." : "KI-Einstellungen speichern"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {activeTab === "ads" && (
        <div className="w-full animate-fade-in">
          <div className="nb-card p-6 bg-white border-2 border-black">
            <h2 className="font-heading font-black text-2xl mb-6 flex items-center gap-2">
              <Megaphone className="h-6 w-6 text-primary" /> Werbung
            </h2>

            {loadingAds ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <form onSubmit={handleSaveAds} className="space-y-4">
                <label className="flex items-center gap-2 font-bold text-sm">
                  <input
                    type="checkbox"
                    checked={adsEnabled}
                    onChange={(e) => setAdsEnabled(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Werbung aktiv
                </label>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Dashboard</label>
                  <textarea
                    value={dashboardSnippet}
                    onChange={(e) => setDashboardSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint im freien Feld des Kurs-Rasters auf dem Dashboard (max. 400×600px). Bei mehr Kursen rutscht es automatisch mit.
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Kursübersicht</label>
                  <textarea
                    value={coursesSnippet}
                    onChange={(e) => setCoursesSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint im freien Feld des Kurs-Rasters auf der "Alle Kurse"-Seite (max. 400×600px).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: unter Lektionen</label>
                  <textarea
                    value={lessonSnippet}
                    onChange={(e) => setLessonSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint unter jeder Lektion, nach dem Quiz (max. 400×600px).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Playground (unter Snippets)</label>
                  <textarea
                    value={playgroundSnippet}
                    onChange={(e) => setPlaygroundSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint unter "Meine Snippets" im Playground (max. 400×600px).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Bestenliste links</label>
                  <textarea
                    value={leaderboardLeftSnippet}
                    onChange={(e) => setLeaderboardLeftSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint links neben der Bestenliste (nur auf großen Bildschirmen).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Bestenliste rechts</label>
                  <textarea
                    value={leaderboardRightSnippet}
                    onChange={(e) => setLeaderboardRightSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint rechts neben der Bestenliste (nur auf großen Bildschirmen).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Banner: Hauptseite</label>
                  <textarea
                    value={landingSnippet}
                    onChange={(e) => setLandingSnippet(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script>...</script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheint auf der oeffentlichen Startseite (https://devpath.kaidra.app/), auch fuer nicht angemeldete Besucher.
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold">Werbeseite (beliebig viele Banner)</label>
                    <button
                      type="button"
                      onClick={addWerbeseiteSnippet}
                      className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 border-2 border-black rounded-md bg-accent nb-press"
                    >
                      <Plus className="h-3 w-3" strokeWidth={3} /> Banner
                    </button>
                  </div>
                  <div className="space-y-2">
                    {werbeseiteSnippets.map((s, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <textarea
                          value={s}
                          onChange={(e) => updateWerbeseiteSnippet(i, e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                          placeholder={`Banner ${i + 1}: <script>...</script>`}
                        />
                        <button
                          type="button"
                          onClick={() => removeWerbeseiteSnippet(i)}
                          disabled={werbeseiteSnippets.length <= 1}
                          className="p-1.5 border-2 border-black rounded-md bg-white hover:bg-destructive hover:text-white disabled:opacity-30 shrink-0"
                          title="Entfernen"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Erscheinen alle untereinander auf der eigenen "Werbeseite" im Hauptmenue.
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Popunder (nur Login-Seite)</label>
                  <textarea
                    value={popunderSnippet}
                    onChange={(e) => setPopunderSnippet(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-xs font-mono"
                    placeholder={'<script src="..."></script>'}
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Wird genau einmal pro Browser ausgeloest, sobald jemand die Login-Seite besucht (per localStorage gemerkt).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Smartlink-URL (optional)</label>
                  <input
                    type="url"
                    value={smartlinkUrl}
                    onChange={(e) => setSmartlinkUrl(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                    placeholder="https://www.effectivecpmnetwork.com/..."
                  />
                  <span className="text-[10px] font-medium text-muted-foreground block mt-0.5">
                    Wird als klar gekennzeichneter, freiwilliger Link im Footer angezeigt (kein Zwang, kein Redirect).
                  </span>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">Smartlink-Beschriftung</label>
                  <input
                    type="text"
                    value={smartlinkLabel}
                    onChange={(e) => setSmartlinkLabel(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black rounded-lg text-sm font-bold"
                    placeholder="Sponsored"
                  />
                </div>

                <button
                  type="submit"
                  disabled={savingAds}
                  className="w-full py-2.5 bg-primary text-white border-2 border-black rounded-lg font-bold text-sm nb-shadow-sm nb-press hover:bg-black hover:text-white transition-all disabled:opacity-50"
                >
                  {savingAds ? "Speichern..." : "Werbe-Einstellungen speichern"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
